import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Wrench, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export function OnsiteRequest({ token, booking, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ address_area: "", best_times: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (window.location.hash === "#onsite" && box.current) { box.current.scrollIntoView({ behavior: "smooth", block: "center" }); setOpen(true); }
  }, []);

  const req = booking.onsite_request;
  if (!req && !booking.can_request_onsite) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (form.address_area.trim().length < 3) return toast.error("Tell us the address or area for the visit");
    setBusy(true);
    try {
      const r = await api.post(`/bookings/public/${token}/onsite`, form);
      onChange(r.data);
      toast.success("Request sent — David will call you with a quote");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send the request — call 310-422-0669"); }
    finally { setBusy(false); }
  };

  if (req) {
    return (
      <div ref={box} id="onsite" className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100 flex items-start gap-3" data-testid="onsite-requested">
        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">On-site visit requested {new Date(req.requested_at).toLocaleDateString()}.</div>
          <div className="mt-1 text-emerald-100/80">David will call you with a price for the visit. Nothing is dispatched until you approve the price.
            {req.ticket_url && <> Track it here: <a href={req.ticket_url} className="dg-link">{req.ticket_id}</a>.</>}</div>
        </div>
      </div>
    );
  }

  return (
    <div ref={box} id="onsite" className="mt-6 rounded-xl border border-[#FFD600]/30 bg-[#FFD600]/5 p-5" data-testid="onsite-box">
      <div className="flex items-start gap-3">
        <Wrench className="w-5 h-5 text-[#FFD600] mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold text-white">Still not fixed? Request an on-site visit.</div>
          <p className="text-sm text-slate-300 mt-1">If the remote session couldn't fully solve it, David can send a technician to finish the job in person (LA area) for a separate service charge. You'll get the price by phone first — nothing is dispatched until you say yes.</p>
          {!open && (
            <button type="button" onClick={() => setOpen(true)} className="dg-btn-primary rounded-full px-5 py-2 text-sm mt-4" data-testid="onsite-open-btn">Request an on-site visit</button>
          )}
          {open && (
            <form onSubmit={submit} className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="onsite-form">
              <label className="block sm:col-span-2"><div className="dg-eyebrow mb-1">Address or area for the visit</div>
                <input className="dg-input" value={form.address_area} onChange={(e) => setForm({ ...form, address_area: e.target.value })} placeholder="e.g. 1234 Main St, Torrance — or just the neighborhood" data-testid="onsite-address" required /></label>
              <label className="block sm:col-span-2"><div className="dg-eyebrow mb-1">Best days & times (optional)</div>
                <input className="dg-input" value={form.best_times} onChange={(e) => setForm({ ...form, best_times: e.target.value })} placeholder="e.g. weekday afternoons, Saturday morning" data-testid="onsite-times" /></label>
              <label className="block sm:col-span-2"><div className="dg-eyebrow mb-1">Anything David should know (optional)</div>
                <textarea className="dg-input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="What's still not working, gate codes, parking, pets…" data-testid="onsite-notes" /></label>
              <div className="sm:col-span-2 flex items-center gap-3">
                <button type="submit" disabled={busy} className="dg-btn-primary rounded-full px-6 py-2 text-sm disabled:opacity-50" data-testid="onsite-submit">{busy ? "Sending…" : "Send request"}</button>
                <button type="button" onClick={() => setOpen(false)} className="text-sm text-slate-400 hover:text-slate-200">Cancel</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
